/**
 * Validation Schemas - Life OS
 *
 * Zod-based validation for all domain types.
 * Validates input at boundaries to prevent bad data from entering the system.
 */

import { z } from 'zod';

// ============================================================
// DEFENSIVE LIMITS
// ============================================================

const MAX_STRING_LENGTH = 10_000;
const MAX_SHORT_STRING = 255;
const MAX_ARRAY_SIZE = 100;

// ============================================================
// COMMON PATTERNS
// ============================================================

const nonEmptyString = (fieldName: string, maxLength = MAX_SHORT_STRING) =>
  z
    .string()
    .min(1, `${fieldName} is required`)
    .max(maxLength, `${fieldName} must be at most ${maxLength} characters`);

const optionalString = (maxLength = MAX_SHORT_STRING) =>
  z.string().max(maxLength).optional();

const textField = (fieldName: string, maxLength = MAX_STRING_LENGTH) =>
  z.string().max(maxLength, `${fieldName} must be at most ${maxLength} characters`);

const positiveNumber = (fieldName: string) =>
  z.number().positive(`${fieldName} must be a positive number`);

const nonNegativeNumber = (fieldName: string) =>
  z.number().nonnegative(`${fieldName} cannot be negative`);

const rating1to5 = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);

const isoDateString = z.string().refine(
  (val) => !isNaN(Date.parse(val)),
  { message: 'Must be a valid date string' }
);

const uuid = z.string().uuid('Must be a valid UUID');

// ============================================================
// ENUMS
// ============================================================

export const CareerPathSchema = z.enum([
  'climb-ladder', 'executive-track', 'management-track', 'technical-specialist',
  'entrepreneurship', 'freelance-consulting', 'portfolio-career',
  'remote-first', 'work-life-balance', 'fire',
  'career-pivot', 'international', 'academic-research', 'public-sector', 'creative-industries',
  'undecided', 'dubai-audit', 'miami-law',
]);

export const CheckinFrequencySchema = z.enum(['daily', 'weekdays', 'three-per-week', 'weekly']);
export const GoalPrioritySchema = z.enum(['critical', 'high', 'medium', 'low']);

export const GoalCategorySchema = z.enum([
  'career',
  'financial',
  'credential',
  'networking',
  'personal',
]);

export const GoalStatusSchema = z.enum([
  'not-started',
  'in-progress',
  'completed',
  'blocked',
  'paused',
  'abandoned',
]);

export const FinancialTypeSchema = z.enum([
  'income',
  'expense',
  'savings',
  'investment',
]);

export const FinancialTargetCategorySchema = z.enum([
  'emergency-fund',
  'law-school',
  'relocation',
  'investment',
  'other',
]);

export const ActionPrioritySchema = z.enum(['high', 'medium', 'low']);

export const ActionStatusSchema = z.enum([
  'pending',
  'in-progress',
  'completed',
  'skipped',
]);

export const ActionCategorySchema = z.enum(['daily', 'weekly', 'milestone']);

export const ChatRoleSchema = z.enum(['user', 'coach']);

export const ChatContextTypeSchema = z.enum([
  'check-in',
  'goal-update',
  'reflection',
  'advice',
  'celebration',
]);

export const RelationshipStrengthSchema = z.enum(['cold', 'warm', 'strong']);

export const ContactCategorySchema = z.enum([
  'recruiter',
  'peer',
  'mentor',
  'target-company',
  'other',
]);

// ============================================================
// PASSWORD SCHEMA
// ============================================================

export const PasswordSchema = z.string()
  .min(12, 'Password must be at least 12 characters')
  .refine(val => /[A-Z]/.test(val), 'Must contain an uppercase letter')
  .refine(val => /[a-z]/.test(val), 'Must contain a lowercase letter')
  .refine(val => /[0-9]/.test(val), 'Must contain a number')
  .refine(val => /[^A-Za-z0-9]/.test(val), 'Must contain a special character');

export const passwordRules = [
  { label: 'At least 12 characters', test: (v: string) => v.length >= 12 },
  { label: 'Uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'Lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'Number', test: (v: string) => /[0-9]/.test(v) },
  { label: 'Special character', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

// ============================================================
// PRIVACY & COMMUNICATION SCHEMAS
// ============================================================

export const PrivacyPreferencesSchema = z.object({
  showProfilePublic: z.boolean(),
  shareProgressWithCoach: z.boolean(),
  allowDataAnalytics: z.boolean(),
  showActivityStatus: z.boolean(),
});

export const CommunicationStyleSchema = z.object({
  tone: z.enum(['direct', 'balanced', 'gentle']),
  detailLevel: z.enum(['brief', 'moderate', 'thorough']),
  motivationStyle: z.enum(['tough-love', 'encouraging', 'analytical']),
  responseLength: z.enum(['concise', 'moderate', 'detailed']),
});

export const FinancialBaselineSchema = z.object({
  monthlySavings: nonNegativeNumber('Monthly savings'),
  monthlyExpenses: nonNegativeNumber('Monthly expenses'),
  totalDebt: nonNegativeNumber('Total debt'),
  emergencyFundMonths: nonNegativeNumber('Emergency fund months'),
  investmentAccounts: z.boolean(),
});

// ============================================================
// DOMAIN SCHEMAS
// ============================================================

export const UserProfileSchema = z.object({
  name: nonEmptyString('Name'),
  currentRole: nonEmptyString('Current role'),
  currentSalary: nonNegativeNumber('Current salary'),
  company: nonEmptyString('Company'),
  location: nonEmptyString('Location'),
  chosenPath: CareerPathSchema,
  chosenPaths: z.array(CareerPathSchema).max(3).default([]),
  targetSalary: positiveNumber('Target salary'),
  targetYear: z.number().int().min(2024).max(2050),
  startDate: isoDateString,
  phone: optionalString(),
  timezone: optionalString(),
  photoUrl: optionalString(),
  bio: optionalString(500),
  careerTarget: optionalString(1000),
  checkinFrequency: CheckinFrequencySchema.optional(),
});

export const MilestoneSchema = z.object({
  id: uuid,
  title: nonEmptyString('Milestone title'),
  completed: z.boolean(),
  completedAt: isoDateString.optional(),
});

export const GoalSchema = z.object({
  id: uuid,
  title: nonEmptyString('Goal title'),
  description: textField('Description', 1000),
  category: GoalCategorySchema,
  targetDate: isoDateString,
  status: GoalStatusSchema,
  progress: z.number().min(0).max(100),
  milestones: z.array(MilestoneSchema).max(MAX_ARRAY_SIZE),
  priority: GoalPrioritySchema,
  archived: z.boolean(),
  archivedAt: isoDateString.optional(),
  createdAt: isoDateString,
  updatedAt: isoDateString,
});

export const DailyCheckInSchema = z.object({
  id: uuid,
  date: isoDateString,
  mood: rating1to5,
  energyLevel: rating1to5,
  tasksCompleted: z.array(z.string().max(500)).max(50),
  blockers: z.array(z.string().max(500)).max(20),
  wins: z.array(z.string().max(500)).max(20),
  reflection: textField('Reflection', 5000),
  tomorrowPriorities: z.array(z.string().max(500)).max(10),
});

export const WeeklyReviewSchema = z.object({
  id: uuid,
  weekOf: isoDateString,
  goalsProgress: z.array(
    z.object({
      goalId: uuid,
      progress: z.number().min(0).max(100),
    })
  ).max(50),
  keyWins: z.array(z.string().max(500)).max(20),
  challenges: z.array(z.string().max(500)).max(20),
  lessonsLearned: z.array(z.string().max(500)).max(20),
  nextWeekFocus: z.array(z.string().max(500)).max(10),
  overallRating: rating1to5,
});

export const FinancialEntrySchema = z.object({
  id: uuid,
  date: isoDateString,
  type: FinancialTypeSchema,
  category: nonEmptyString('Category', 100),
  amount: z.number(),
  description: textField('Description', 500),
  recurring: z.boolean(),
});

export const FinancialTargetSchema = z.object({
  id: uuid,
  name: nonEmptyString('Target name'),
  targetAmount: positiveNumber('Target amount'),
  currentAmount: nonNegativeNumber('Current amount'),
  deadline: isoDateString,
  category: FinancialTargetCategorySchema,
});

export const ActionItemSchema = z.object({
  id: uuid,
  title: nonEmptyString('Task title'),
  description: optionalString(1000),
  dueDate: isoDateString,
  priority: ActionPrioritySchema,
  status: ActionStatusSchema,
  category: ActionCategorySchema,
  dayNumber: z.number().int().min(1).max(30).optional(),
  completedAt: isoDateString.optional(),
});

export const ChatContextSchema = z.object({
  type: ChatContextTypeSchema,
  relatedGoalId: uuid.optional(),
});

export const ChatMessageSchema = z.object({
  id: uuid,
  role: ChatRoleSchema,
  content: textField('Message', 10000),
  timestamp: isoDateString,
  context: ChatContextSchema.optional(),
});

export const NetworkingContactSchema = z.object({
  id: uuid,
  name: nonEmptyString('Contact name'),
  title: nonEmptyString('Title'),
  company: nonEmptyString('Company'),
  location: nonEmptyString('Location'),
  linkedIn: z.string().url().optional().or(z.literal('')),
  email: z.string().email().optional().or(z.literal('')),
  relationship: RelationshipStrengthSchema,
  lastContact: isoDateString.optional(),
  notes: textField('Notes', 2000),
  category: ContactCategorySchema,
});

export const NotificationSettingsSchema = z.object({
  enabled: z.boolean(),
  dailyReminderTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Must be in HH:mm format'),
  reminderTypes: z.object({
    checkIn: z.boolean(),
    tasks: z.boolean(),
    goals: z.boolean(),
    motivational: z.boolean(),
  }),
});

export const AppStateSchema = z.object({
  profile: UserProfileSchema,
  goals: z.array(GoalSchema).max(MAX_ARRAY_SIZE),
  dailyCheckIns: z.array(DailyCheckInSchema).max(365),
  weeklyReviews: z.array(WeeklyReviewSchema).max(52),
  financialEntries: z.array(FinancialEntrySchema).max(1000),
  financialTargets: z.array(FinancialTargetSchema).max(50),
  actionItems: z.array(ActionItemSchema).max(500),
  chatHistory: z.array(ChatMessageSchema).max(1000),
  networkingContacts: z.array(NetworkingContactSchema).max(500),
  notificationSettings: NotificationSettingsSchema,
});

// ============================================================
// INPUT SCHEMAS (for form validation - partial/create)
// ============================================================

export const CreateGoalInputSchema = GoalSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  progress: true,
}).extend({
  milestones: z.array(MilestoneSchema.omit({ id: true, completed: true, completedAt: true }).extend({
    title: nonEmptyString('Milestone title'),
  })).min(1, 'At least one milestone is required').max(20),
});

export const UpdateGoalInputSchema = GoalSchema.partial().extend({
  id: uuid,
});

export const CreateActionItemInputSchema = ActionItemSchema.omit({
  id: true,
  completedAt: true,
});

export const CreateFinancialEntryInputSchema = FinancialEntrySchema.omit({
  id: true,
});

export const CreateNetworkingContactInputSchema = NetworkingContactSchema.omit({
  id: true,
});

export const DailyCheckInInputSchema = DailyCheckInSchema.omit({
  id: true,
});

// ============================================================
// VALIDATION HELPERS
// ============================================================

/** Validation error structure */
export interface ValidationError {
  path: (string | number)[];
  message: string;
}

export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; errors: ValidationError[] };

/**
 * Validate data against a schema and return typed result
 */
export function validate<T>(
  schema: z.ZodType<T>,
  data: unknown
): ValidationResult<T> {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  // Zod v4 uses 'issues' property
  const issues = result.error.issues || (result.error as unknown as { errors: z.ZodIssue[] }).errors || [];
  return {
    success: false,
    errors: issues.map((issue: z.ZodIssue) => ({
      path: issue.path.map((p) => (typeof p === 'symbol' ? String(p) : p)),
      message: issue.message,
    })),
  };
}

/**
 * Format validation errors for display
 */
export function formatErrors(errors: ValidationError[]): string[] {
  return errors.map((err) => {
    const path = err.path.join('.');
    return path ? `${path}: ${err.message}` : err.message;
  });
}

/**
 * Validate and throw if invalid (for internal use)
 */
export function validateOrThrow<T>(schema: z.ZodType<T>, data: unknown): T {
  return schema.parse(data);
}

// ============================================================
// TYPE EXPORTS (inferred from schemas)
// ============================================================

export type CareerPath = z.infer<typeof CareerPathSchema>;
export type GoalCategory = z.infer<typeof GoalCategorySchema>;
export type GoalStatus = z.infer<typeof GoalStatusSchema>;
export type UserProfile = z.infer<typeof UserProfileSchema>;
export type Milestone = z.infer<typeof MilestoneSchema>;
export type Goal = z.infer<typeof GoalSchema>;
export type DailyCheckIn = z.infer<typeof DailyCheckInSchema>;
export type WeeklyReview = z.infer<typeof WeeklyReviewSchema>;
export type FinancialEntry = z.infer<typeof FinancialEntrySchema>;
export type FinancialTarget = z.infer<typeof FinancialTargetSchema>;
export type ActionItem = z.infer<typeof ActionItemSchema>;
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
export type NetworkingContact = z.infer<typeof NetworkingContactSchema>;
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;
export type AppState = z.infer<typeof AppStateSchema>;
export type CreateGoalInput = z.infer<typeof CreateGoalInputSchema>;
export type UpdateGoalInput = z.infer<typeof UpdateGoalInputSchema>;
export type CreateActionItemInput = z.infer<typeof CreateActionItemInputSchema>;
export type DailyCheckInInput = z.infer<typeof DailyCheckInInputSchema>;
